/**
 * Clean wrapper around @microsoft/teams-js v2 (promise-based API).
 * The old callback-style authentication.getAuthToken({ successCallback, failureCallback })
 * was removed in SDK v2; this file uses only the v2 promise interface.
 */
import { app, authentication } from "@microsoft/teams-js";

let _initPromise: Promise<boolean> | null = null;
/** Cached result after SDK init or heuristic (client-only). */
let _teamsHostDetected: boolean | null = null;

const TEAMS_HOST_PATTERN =
  /teams\.microsoft\.com|teams\.cloud\.microsoft|local\.teams\.office\.com|teams\.live\.com/i;

/**
 * Synchronous fallback when the Teams JS SDK is slow or fails to initialize.
 * Trek's CSP only allows Teams hosts in frame-ancestors, so an iframe embed is
 * almost always the Teams client.
 */
export function isTeamsHostHeuristic(): boolean {
  if (typeof window === "undefined") return false;

  const ua = navigator.userAgent ?? "";
  if (/Teams/i.test(ua) || /Microsoft Teams/i.test(ua)) return true;

  const ref = document.referrer ?? "";
  if (TEAMS_HOST_PATTERN.test(ref)) return true;

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has("teamsContext")) return true;
  } catch {
    /* ignore */
  }

  try {
    if (window.self !== window.top) return true;
  } catch {
    // Cross-origin parent access blocked — typical for Teams tab iframes.
    return true;
  }

  return false;
}

/** Whether the app is running inside a Microsoft Teams host (sync, safe for render). */
export function isInTeamsHost(): boolean {
  if (_teamsHostDetected !== null) return _teamsHostDetected;
  if (typeof window === "undefined") return false;
  return isTeamsHostHeuristic();
}

function cacheTeamsHostDetected(value: boolean): void {
  _teamsHostDetected = value;
}

/**
 * Initialize the Teams JS SDK exactly once.
 * Returns true when running inside a Teams host, false otherwise.
 */
export async function initializeTeams(): Promise<boolean> {
  if (_initPromise) return _initPromise;

  _initPromise = app
    .initialize()
    .then(async () => {
      // Successful initialize() only resolves inside a Teams (or M365) host.
      try {
        const ctx = await app.getContext();
        const hostName = ctx?.app?.host?.name;
        cacheTeamsHostDetected(Boolean(hostName) || isTeamsHostHeuristic());
      } catch {
        cacheTeamsHostDetected(true);
      }
      return _teamsHostDetected === true;
    })
    .catch(() => {
      _initPromise = null; // allow retry if init fails
      const detected = isTeamsHostHeuristic();
      cacheTeamsHostDetected(detected);
      return detected;
    });

  return _initPromise;
}

/** Full Teams app context (tab name, theme, locale, meeting ID, etc.). */
export async function getTeamsContext(): Promise<app.Context | null> {
  try {
    return await app.getContext();
  } catch {
    return null;
  }
}

/**
 * Request an SSO ID-token from Teams for the configured AAD resource
 * (webApplicationInfo in the manifest).  Throws the Teams SDK error code
 * string on failure (e.g. "resourceDisabled", "notSupported").
 */
export async function getTeamsToken(): Promise<string> {
  const token = await authentication.getAuthToken();
  if (!token) throw new Error("Teams getAuthToken returned an empty token");
  return token;
}

/**
 * Tell Teams the tab has finished loading.
 * Safe to call outside Teams — errors are silently ignored.
 */
export function notifyAppLoaded(): void {
  try {
    app.notifyAppLoaded();
    app.notifySuccess();
  } catch {
    // not in Teams — ignore
  }
}
